import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { APP_ROUTES } from "../constants";
import { useStore } from "../store/globalState";
import {
  buildAlreadyFundedErrorState,
  isCashDepositAlreadyFunded,
} from "../utils/already-funded";
import { useCashDepositContext } from "./useCashDepositContext";
import { useLiveExitUrl } from "./useLiveExitUrl";
import { normalizeRoutePath } from "../utils/route-path";
import { useRouteWithToken } from "./useRouteWithToken";

const ALLOWED_WHILE_ALREADY_FUNDED = new Set<string>([
  APP_ROUTES.LOADING_PAGE,
  APP_ROUTES.ERROR_PAGE,
  APP_ROUTES.SESSION_EXPIRED_PAGE,
  APP_ROUTES.SUPPORT_PAGE,
  APP_ROUTES.PAYMENT_VERIFICATION_PAGE,
  APP_ROUTES.PHONE_PAGE,
  APP_ROUTES.DEPOSIT_ON_THE_WAY_PAGE,
]);

const KNOWN_FLOW_PATHS = new Set<string>(Object.values(APP_ROUTES));

/**
 * After splash we already know whether the pinned operation can mint a
 * barcode. Deep links to landing / stores bounce here so the user cannot
 * walk the deposit flow again.
 */
export function useAlreadyFundedGate() {
  const { t } = useTranslation();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const { context } = useCashDepositContext();
  const callbackUrl = useLiveExitUrl();
  const setErrorState = useStore((s) => s.setErrorState);
  const pathnameRef = useRef(location.pathname);
  const routedRef = useRef(false);

  useEffect(() => {
    const path = normalizeRoutePath(location.pathname);
    pathnameRef.current = path;
    if (ALLOWED_WHILE_ALREADY_FUNDED.has(path)) {
      routedRef.current = false;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isCashDepositAlreadyFunded(context) || routedRef.current) return;
    const path = pathnameRef.current;
    if (ALLOWED_WHILE_ALREADY_FUNDED.has(path)) return;
    // `/:linkId` is the splash entry — leave LoadingPage to route after splash.
    if (!KNOWN_FLOW_PATHS.has(path)) return;
    routedRef.current = true;
    const error = buildAlreadyFundedErrorState(t, callbackUrl);
    setErrorState(error);
    redirectTo(APP_ROUTES.ERROR_PAGE, { replace: true, state: error });
  }, [context, callbackUrl, location.pathname, redirectTo, setErrorState, t]);
}
